import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { MoneyInput, NumInput } from '../ui/Input'
import { fmt, fmtS, fmtN, fmtCoef, pN, uid } from '../../utils/formatters'
import { exportRABToPDF } from '../../utils/exportPDF'
import { exportRABToExcel } from '../../utils/exportExcel'
import PresetManager from './PresetManager'

const RAB_CATS = [
  'Pekerjaan Persiapan', 'Pekerjaan Tanah', 'Pekerjaan Struktur',
  'Pekerjaan Dinding', 'Pekerjaan Atap', 'Pekerjaan Lantai',
  'Pekerjaan Plafon', 'Pekerjaan Cat', 'Instalasi ME', 'Pekerjaan Finishing'
]

const PRESETS = {
  bahan: [
    // ─── Semen & Pasir ───
    { name: 'Semen PC 50kg', unit: 'sak', price: 65000 },
    { name: 'Semen Portland Type I', unit: 'sak', price: 68000 },
    { name: 'Pasir Pasang', unit: 'm³', price: 300000 },
    { name: 'Pasir Beton', unit: 'm³', price: 320000 },
    { name: 'Pasir Halus (Plesteran)', unit: 'm³', price: 280000 },
    { name: 'Koral/Split 2-3 cm', unit: 'm³', price: 350000 },
    // ─── Batu & Bata ───
    { name: 'Bata Merah Standar', unit: 'bh', price: 800 },
    { name: 'Bata Ringan 10cm (AAC)', unit: 'bh', price: 8500 },
    { name: 'Bata Ringan 7.5cm (AAC)', unit: 'bh', price: 7000 },
    { name: 'Batako Press', unit: 'bh', price: 3500 },
    { name: 'Batu Alam (Andesit)', unit: 'm²', price: 250000 },
    { name: 'Batu Kali', unit: 'm³', price: 180000 },
    // ─── Besi & Beton ───
    { name: 'Besi Beton Ø8mm', unit: 'kg', price: 13500 },
    { name: 'Besi Beton Ø10mm', unit: 'kg', price: 13500 },
    { name: 'Besi Beton Ø12mm', unit: 'kg', price: 14000 },
    { name: 'Besi Beton Ø16mm', unit: 'kg', price: 14500 },
    { name: 'Besi WF/Baja', unit: 'kg', price: 18000 },
    { name: 'Kawat Bendrat', unit: 'kg', price: 22000 },
    { name: 'Ready Mix Beton K-250', unit: 'm³', price: 950000 },
    { name: 'Ready Mix Beton K-300', unit: 'm³', price: 1050000 },
    // ─── Kayu & Triplek ───
    { name: 'Kayu Bekisting (Meranti)', unit: 'm³', price: 4500000 },
    { name: 'Kayu Usuk 5/7', unit: 'btg', price: 35000 },
    { name: 'Kayu Reng 3/5', unit: 'btg', price: 18000 },
    { name: 'Kayu Balok 6/12', unit: 'btg', price: 120000 },
    { name: 'Triplex 9mm', unit: 'lbr', price: 130000 },
    { name: 'Multiplex 12mm', unit: 'lbr', price: 180000 },
    { name: 'GRC Board 6mm', unit: 'lbr', price: 95000 },
    { name: 'Gypsum Board 9mm', unit: 'lbr', price: 85000 },
    // ─── Keramik & Granit ───
    { name: 'Keramik Lantai 40x40', unit: 'm²', price: 75000 },
    { name: 'Keramik Lantai 60x60', unit: 'm²', price: 120000 },
    { name: 'Granit 60x60', unit: 'm²', price: 200000 },
    { name: 'Keramik Dinding 25x40', unit: 'm²', price: 65000 },
    { name: 'Nat Keramik', unit: 'kg', price: 12000 },
    { name: 'Perekat Keramik', unit: 'sak', price: 55000 },
    // ─── Cat & Finishing ───
    { name: 'Cat Tembok Interior (15L)', unit: 'gln', price: 450000 },
    { name: 'Cat Tembok Eksterior (15L)', unit: 'gln', price: 550000 },
    { name: 'Cat Dasar/Plamir', unit: 'kg', price: 35000 },
    { name: 'Cat Kayu/Besi (1L)', unit: 'ltr', price: 85000 },
    { name: 'Acian Semen Putih', unit: 'sak', price: 95000 },
    // ─── Atap ───
    { name: 'Genteng Metal Berpasir', unit: 'm²', price: 85000 },
    { name: 'Genteng Beton', unit: 'bh', price: 5500 },
    { name: 'Genteng Keramik', unit: 'bh', price: 9000 },
    { name: 'Atap Galvalum 0.3mm', unit: 'm²', price: 75000 },
    { name: 'Atap Onduline', unit: 'lbr', price: 140000 },
    { name: 'Nok Atap Metal', unit: 'btg', price: 55000 },
    // ─── Kusen, Pintu & Jendela ───
    { name: 'Kusen Aluminium 4"', unit: 'm\'', price: 165000 },
    { name: 'Pintu Panel Kayu', unit: 'unit', price: 1800000 },
    { name: 'Daun Pintu Multipleks', unit: 'unit', price: 850000 },
    { name: 'Kaca Polos 5mm', unit: 'm²', price: 120000 },
    { name: 'Kaca Ryben 5mm', unit: 'm²', price: 145000 },
    // ─── Plumbing / Sanitasi ───
    { name: 'Pipa PVC AW 3/4"', unit: 'btg', price: 38000 },
    { name: 'Pipa PVC AW 1"', unit: 'btg', price: 55000 },
    { name: 'Pipa PVC D 4" (Buangan)', unit: 'btg', price: 85000 },
    { name: 'Kloset Duduk', unit: 'unit', price: 1500000 },
    { name: 'Wastafel', unit: 'unit', price: 650000 },
    { name: 'Floor Drain', unit: 'bh', price: 45000 },
    // ─── Elektrikal ───
    { name: 'Kabel NYM 2x2.5mm', unit: 'm\'', price: 12000 },
    { name: 'Kabel NYM 3x2.5mm', unit: 'm\'', price: 16000 },
    { name: 'Stop Kontak', unit: 'bh', price: 45000 },
    { name: 'Saklar Tunggal', unit: 'bh', price: 35000 },
    { name: 'Lampu Downlight LED 9W', unit: 'bh', price: 55000 },
    { name: 'Pipa Conduit 5/8"', unit: 'btg', price: 22000 },
  ],
  upah: [
    // ─── Tenaga ───
    { name: 'Kepala Tukang', unit: 'OH', price: 400000 },
    { name: 'Mandor', unit: 'OH', price: 350000 },
    { name: 'Tukang Batu/Bata', unit: 'OH', price: 250000 },
    { name: 'Tukang Besi/Cor', unit: 'OH', price: 270000 },
    { name: 'Tukang Kayu/Bekisting', unit: 'OH', price: 260000 },
    { name: 'Tukang Cat', unit: 'OH', price: 230000 },
    { name: 'Tukang Keramik', unit: 'OH', price: 260000 },
    { name: 'Tukang Las/Baja', unit: 'OH', price: 300000 },
    { name: 'Tukang Listrik', unit: 'OH', price: 300000 },
    { name: 'Tukang Plumbing/Ledeng', unit: 'OH', price: 280000 },
    { name: 'Tukang Atap', unit: 'OH', price: 270000 },
    { name: 'Tukang Acian', unit: 'OH', price: 260000 },
    { name: 'Pekerja/Kenek', unit: 'OH', price: 180000 },
    { name: 'Operator Alat Berat', unit: 'OH', price: 450000 },
  ],
  alat: [
    // ─── Alat Berat ───
    { name: 'Excavator', unit: 'jam', price: 450000 },
    { name: 'Bulldozer', unit: 'jam', price: 600000 },
    { name: 'Dump Truck 8m³', unit: 'rit', price: 350000 },
    { name: 'Vibro Roller', unit: 'jam', price: 400000 },
    { name: 'Tower Crane', unit: 'hari', price: 2500000 },
    // ─── Alat Kecil ───
    { name: 'Concrete Mixer 0.35m³', unit: 'jam', price: 150000 },
    { name: 'Concrete Pump', unit: 'jam', price: 850000 },
    { name: 'Scaffolding (Steger)', unit: 'stel/bln', price: 350000 },
    { name: 'Stamper Tanah (Pemadat)', unit: 'jam', price: 120000 },
    { name: 'Mesin Genset 5kWh', unit: 'hari', price: 300000 },
    { name: 'Bar Bender', unit: 'jam', price: 85000 },
    { name: 'Bar Cutter', unit: 'jam', price: 85000 },
    { name: 'Vibrator Beton', unit: 'jam', price: 65000 },
    { name: 'Mesin Las Listrik', unit: 'jam', price: 95000 },
    { name: 'Waterpass (theodolite)', unit: 'hari', price: 250000 },
  ]
}

const TYPE_COLORS = {
  bahan: { bg: 'var(--info-bg)', c: 'var(--info)', label: '🧱 BAHAN' },
  upah: { bg: 'var(--success-bg)', c: 'var(--success)', label: '👷 UPAH' },
  alat: { bg: 'var(--purple-bg)', c: 'var(--purple)', label: '🔧 ALAT' }
}

export default function RAB() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [rabItems, setRabItems] = useState([])
  const [rabComponents, setRabComponents] = useState([])
  const [projects, setProjects] = useState([])
  const [selProj, setSelProj] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [edit, setEdit] = useState(null)
  const [expandedCats, setExpandedCats] = useState({})
  const [expandedItems, setExpandedItems] = useState({})
  const [oh, setOh] = useState(10)
  const [ppn, setPpn] = useState(11)

  const [showAddComp, setShowAddComp] = useState(false)
  const [showPreset, setShowPreset] = useState(false)
  const [showPresetMgr, setShowPresetMgr] = useState(false)
  const [userPresets, setUserPresets] = useState({ bahan: [], upah: [], alat: [] })
  const [compForm, setCompForm] = useState({
    type: 'bahan', name: '', unit: '', coefficient: '', price: 0, real_price: 0
  })

  const blank = {
    project_id: '', category: RAB_CATS[0], item: '', unit: 'm²',
    volume: 0, real_volume: 0
  }
  const [form, setForm] = useState(blank)
  const [itemComponents, setItemComponents] = useState([])

  useEffect(() => {
    if (!user) return
    fetchData()
  }, [user])

  useEffect(() => {
    if (projects.length && !selProj) {
      setSelProj(projects[0].id)
    }
  }, [projects])

  const fetchData = async () => {
    const [{ data: items }, { data: comps }, { data: proj }, { data: userPre }] = await Promise.all([
      supabase.from('rab_items').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      supabase.from('rab_components').select('*'),
      supabase.from('projects').select('*').eq('user_id', user.id),
      supabase.from('preset_items').select('*').eq('user_id', user.id).order('name'),
    ])
    if (items) setRabItems(items)
    if (comps) setRabComponents(comps)
    if (proj) setProjects(proj)
    if (userPre) {
      const grouped = { bahan: [], upah: [], alat: [] }
      userPre.forEach(p => { if (grouped[p.type]) grouped[p.type].push(p) })
      setUserPresets(grouped)
    }
  }

  const getComponentsForItem = (itemId) => {
    return rabComponents.filter(c => c.rab_item_id === itemId)
  }

  const calcUP = (comps, field = 'price') => {
    return comps.reduce((s, c) => s + (c.coefficient || 0) * (c[field] || c.price || 0), 0)
  }

  const calcItemBudget = (item, comps) => {
    return (item.volume || 0) * calcUP(comps, 'price')
  }

  const calcItemReal = (item, comps) => {
    return (item.real_volume || 0) * calcUP(comps, 'real_price')
  }

  const toggleCat = (cat) => {
    setExpandedCats(p => ({ ...p, [cat]: !(p[cat] ?? true) }))
  }

  const toggleItem = (id) => {
    setExpandedItems(p => ({ ...p, [id]: !p[id] }))
  }

  const openAdd = (cat) => {
    setForm({ ...blank, category: cat || RAB_CATS[0], project_id: selProj })
    setItemComponents([])
    setEdit(null)
    setShowModal(true)
    setShowAddComp(false)
  }

  const openEdit = (item) => {
    const comps = getComponentsForItem(item.id)
    setForm({ ...item })
    setItemComponents(comps.map(c => ({ ...c })))
    setEdit(item.id)
    setShowModal(true)
    setShowAddComp(false)
  }

  const save = async () => {
    if (!form.item) {
      toast.show('Uraian pekerjaan wajib diisi')
      return
    }

    const data = {
      ...form,
      volume: pN(form.volume),
      real_volume: pN(form.real_volume),
      user_id: user.id
    }

    let itemId = edit
    if (!edit) {
      itemId = uid()
      data.id = itemId
      await supabase.from('rab_items').insert([data])
    } else {
      await supabase.from('rab_items').update(data).eq('id', edit)
      // Delete existing components
      await supabase.from('rab_components').delete().eq('rab_item_id', edit)
    }

    // Save components
    for (const comp of itemComponents) {
      await supabase.from('rab_components').insert([{
        id: uid(),
        rab_item_id: itemId,
        ...comp
      }])
    }

    setShowModal(false)
    toast.show(edit ? 'Item diperbarui' : 'Item ditambahkan')
    fetchData()
  }

  const del = async (id) => {
    const ok = await confirm('Hapus item RAB beserta semua komponen AHS-nya?', {
      title: 'Hapus Item RAB', icon: '💰', confirmLabel: 'Hapus', confirmVariant: 'danger'
    })
    if (!ok) return

    await supabase.from('rab_components').delete().eq('rab_item_id', id)
    await supabase.from('rab_items').delete().eq('id', id)

    toast.show('Item dihapus')
    fetchData()
  }

  const addComp = () => {
    if (!compForm.name || !compForm.coefficient) {
      toast.show('Nama dan koefisien wajib diisi')
      return
    }

    const c = {
      ...compForm,
      id: uid(),
      coefficient: parseFloat(compForm.coefficient) || 0,
      price: pN(compForm.price),
      real_price: pN(compForm.real_price) || pN(compForm.price)
    }

    setItemComponents(p => [...p, c])
    setCompForm({ type: compForm.type, name: '', unit: '', coefficient: '', price: 0, real_price: 0 })
    setShowAddComp(false)
  }

  const addPreset = (preset, type) => {
    setCompForm({
      type,
      name: preset.name,
      unit: preset.unit,
      coefficient: '',
      price: preset.price,
      real_price: preset.price
    })
    setShowPreset(false)
    setShowAddComp(true)
  }

  const delComp = (cid) => {
    setItemComponents(p => p.filter(c => c.id !== cid))
  }

  const projItems = rabItems.filter(r => r.project_id === selProj)
  const cats = [...new Set(projItems.map(r => r.category))]

  const catTotals = useMemo(() => {
    const r = {}
    cats.forEach(c => {
      const items = projItems.filter(x => x.category === c)
      r[c] = {
        budget: items.reduce((s, x) => s + calcItemBudget(x, getComponentsForItem(x.id)), 0),
        real: items.reduce((s, x) => s + calcItemReal(x, getComponentsForItem(x.id)), 0),
        count: items.length
      }
    })
    return r
  }, [projItems, cats, rabComponents])

  const gB = Object.values(catTotals).reduce((s, c) => s + c.budget, 0)
  const gR = Object.values(catTotals).reduce((s, c) => s + c.real, 0)
  const gtB = gB * (1 + oh / 100) * (1 + ppn / 100)
  const gtR = gR * (1 + oh / 100) * (1 + ppn / 100)
  const variance = gtB - gtR

  const formBudgetUP = calcUP(itemComponents, 'price')
  const formRealUP = calcUP(itemComponents, 'real_price')
  const formBudgetTotal = pN(form.volume) * formBudgetUP
  const formRealTotal = pN(form.real_volume) * formRealUP

  const handleExportPDF = () => {
    const project = projects.find(p => p.id === selProj)
    const categories = cats.map(name => ({ name, ...catTotals[name] }))
    exportRABToPDF(project, projItems, categories, oh, ppn)
    toast.show('PDF diunduh')
  }

  const handleExportExcel = () => {
    const project = projects.find(p => p.id === selProj)
    const categories = cats.map(name => ({ name, ...catTotals[name] }))
    exportRABToExcel(project, projItems, categories, oh, ppn)
    toast.show('Excel diunduh')
  }

  return (
    <div>
      <div className="filter-row">
        <select
          className="form-select"
          style={{ width: 220 }}
          value={selProj}
          onChange={(e) => setSelProj(e.target.value)}
        >
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <Button variant="secondary" onClick={handleExportExcel}>📊 Excel</Button>
        <Button variant="secondary" onClick={handleExportPDF}>📄 PDF</Button>
        <Button variant="secondary" onClick={() => setShowPresetMgr(true)}>⚙️ Kelola Preset</Button>
        <Button onClick={() => openAdd()}>➕ Item AHS</Button>
      </div>

      {/* Summary Cards */}
      <div className="rab-summary">
        <div className="rab-summary-card">
          <div className="lbl">Anggaran (RAB)</div>
          <div className="val" style={{ color: 'var(--accent)' }}>{fmtS(gtB)}</div>
        </div>
        <div className="rab-summary-card">
          <div className="lbl">Realisasi</div>
          <div className="val" style={{ color: 'var(--info)' }}>{fmtS(gtR)}</div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{gB ? Math.round(gtR / gtB * 100) : 0}%</div>
        </div>
        <div className="rab-summary-card">
          <div className="lbl">Selisih</div>
          <div className="val" style={{ color: variance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {variance >= 0 ? '+' : ''}{fmtS(Math.abs(variance))}
          </div>
        </div>
        <div className="rab-summary-card">
          <div className="lbl">Sisa</div>
          <div className="val" style={{ color: variance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {fmtS(Math.max(0, variance))}
          </div>
          <div className="variance-bar">
            <div
              className="fill"
              style={{
                width: `${gtB ? Math.min(100, gtR / gtB * 100) : 0}%`,
                background: 'var(--info)'
              }}
            />
          </div>
        </div>
      </div>

      {/* OH/PPN Settings */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>⚙ OH & PPN:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ fontSize: 11 }}>OH&P:</label>
            <input
              className="form-input"
              type="number"
              style={{ width: 55, padding: '4px 6px', textAlign: 'center' }}
              value={oh}
              onChange={(e) => setOh(Number(e.target.value))}
            />
            <span style={{ fontSize: 11 }}>%</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <label style={{ fontSize: 11 }}>PPN:</label>
            <input
              className="form-input"
              type="number"
              style={{ width: 55, padding: '4px 6px', textAlign: 'center' }}
              value={ppn}
              onChange={(e) => setPpn(Number(e.target.value))}
            />
            <span style={{ fontSize: 11 }}>%</span>
          </div>
        </div>
      </div>

      {/* Categories & Items */}
      {cats.map((cat, ci) => {
        const ct = catTotals[cat]
        const items = projItems.filter(r => r.category === cat)
        const isExp = expandedCats[cat] !== false

        return (
          <div key={cat} className="rab-cat">
            <div className="rab-cat-head" onClick={() => toggleCat(cat)}>
              <h4>
                <span style={{
                  background: 'var(--accent)',
                  color: '#000',
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 800,
                  flexShrink: 0
                }}>
                  {ci + 1}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cat}
                </span>
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 10 }}>
                  ({ct.count})
                </span>
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ textAlign: 'right' }}>
                  <div className="ct">{fmtS(ct.budget)}</div>
                  <div style={{ fontSize: 9, color: ct.real <= ct.budget ? 'var(--success)' : 'var(--danger)' }}>
                    Real: {fmtS(ct.real)}
                  </div>
                </div>
                {isExp ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ width: 16, height: 16 }}>
                    <path d="M18 15l-6-6-6 6" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ width: 16, height: 16 }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                )}
              </div>
            </div>

            {isExp && (
              <div>
                {items.map((it, idx) => {
                  const comps = getComponentsForItem(it.id)
                  const budgetUP = calcUP(comps, 'price')
                  const realUP = calcUP(comps, 'real_price')
                  const bTotal = calcItemBudget(it, comps)
                  const rTotal = calcItemReal(it, comps)
                  const diff = bTotal - rTotal
                  const isItemExp = expandedItems[it.id]

                  return (
                    <div key={it.id} className="rab-item">
                      <div className="rab-item-head" onClick={() => toggleItem(it.id)}>
                        <div className="rab-item-num">{idx + 1}</div>
                        <div className="rab-item-info">
                          <div className="rab-item-name">{it.item}</div>
                          <div className="rab-item-meta">
                            <span>📐 {fmtN(it.volume)} {it.unit}</span>
                            <span>💰 {fmt(budgetUP)}/{it.unit}</span>
                            <span style={{ color: 'var(--text-muted)' }}>
                              ({comps.length} komponen)
                            </span>
                          </div>
                        </div>
                        <div className="rab-item-price">
                          <div className="up">{fmtS(bTotal)}</div>
                          <div className="tot">
                            {rTotal > 0 ? (
                              <span style={{ color: diff >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                                Real: {fmtS(rTotal)}
                              </span>
                            ) : ''}
                          </div>
                        </div>
                        <div className="rab-item-actions" onClick={(e) => e.stopPropagation()}>
                          <Button size="xs" variant="secondary" onClick={() => openEdit(it)}>
                            ✏️
                          </Button>
                          <Button size="xs" variant="danger" onClick={() => del(it.id)}>
                            🗑️
                          </Button>
                        </div>
                      </div>

                      {isItemExp && (
                        <div className="rab-ahs">
                          {['bahan', 'upah', 'alat'].map(type => {
                            const typeComps = comps.filter(c => c.type === type)
                            if (!typeComps.length) return null

                            const tc = TYPE_COLORS[type]
                            const subB = typeComps.reduce((s, c) => s + c.coefficient * c.price, 0)

                            return (
                              <div key={type} className="ahs-section">
                                <div className={`ahs-section-head ahs-${type}`}>
                                  {tc.label} — Sub: {fmt(subB)}/{it.unit}
                                </div>
                                {typeComps.map(c => (
                                  <div key={c.id} className="ahs-row">
                                    <div className="name">{c.name}</div>
                                    <div className="koef">{fmtCoef(c.coefficient)} {c.unit}</div>
                                    <div className="hrg">× {fmt(c.price)}</div>
                                    <div className="sub">= {fmt(c.coefficient * c.price)}</div>
                                  </div>
                                ))}
                              </div>
                            )
                          })}

                          <div className="ahs-total">
                            <div className="label">HARGA SATUAN PEKERJAAN</div>
                            <div className="value">{fmt(budgetUP)} / {it.unit}</div>
                          </div>

                          <div className="ahs-comparison">
                            <div className="ahs-comp-card" style={{
                              background: 'var(--accent-bg)',
                              border: '1px solid rgba(245,158,11,.2)'
                            }}>
                              <div className="lbl" style={{ color: 'var(--accent)' }}>ANGGARAN</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                                {fmtN(it.volume)} {it.unit} × {fmt(budgetUP)}
                              </div>
                              <div className="val" style={{ color: 'var(--accent)' }}>{fmt(bTotal)}</div>
                            </div>
                            <div className="ahs-comp-card" style={{
                              background: 'var(--info-bg)',
                              border: '1px solid rgba(88,166,255,.2)'
                            }}>
                              <div className="lbl" style={{ color: 'var(--info)' }}>REALISASI</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                                {fmtN(it.real_volume || 0)} {it.unit} × {fmt(realUP)}
                              </div>
                              <div className="val" style={{ color: 'var(--info)' }}>{fmt(rTotal)}</div>
                            </div>
                          </div>

                          {bTotal > 0 && (
                            <div style={{
                              textAlign: 'center',
                              marginTop: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              color: diff >= 0 ? 'var(--success)' : 'var(--danger)'
                            }}>
                              Selisih: {diff >= 0 ? '+' : ''}{fmt(Math.abs(diff))}
                              ({diff >= 0 ? 'hemat' : 'over'} {Math.abs(bTotal ? diff / bTotal * 100 : 0).toFixed(1)}%)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                <div style={{ padding: 6, textAlign: 'center' }}>
                  <Button size="sm" variant="secondary" onClick={() => openAdd(cat)}>
                    ➕ Tambah Item di {cat}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )
      })}

      {!cats.length && (
        <div className="empty-state" style={{ padding: 40 }}>
          <p>Belum ada RAB. Klik "+ Item AHS" untuk mulai.</p>
        </div>
      )}

      {/* Add/Edit Item Modal */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={edit ? '✏️ Edit Item AHS' : '➕ Tambah Item AHS'}
        large
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Batal</Button>
            <Button onClick={save}>💾 Simpan</Button>
          </>
        }
      >
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Kategori</label>
            <select
              className="form-select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {[...new Set([...RAB_CATS, ...cats])].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Satuan Pekerjaan</label>
            <input
              className="form-input"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              placeholder="m², m³, kg, ls"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Uraian Pekerjaan *</label>
          <input
            className="form-input"
            value={form.item}
            onChange={(e) => setForm({ ...form, item: e.target.value })}
            placeholder="Contoh: Pasangan Bata Ringan"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Volume Anggaran</label>
            <NumInput
              value={form.volume}
              onChange={(v) => setForm({ ...form, volume: v })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Volume Realisasi</label>
            <NumInput
              value={form.real_volume}
              onChange={(v) => setForm({ ...form, real_volume: v })}
            />
          </div>
        </div>

        {/* AHS Components Section */}
        <div style={{
          marginTop: 12,
          padding: 12,
          background: 'var(--bg-primary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
            flexWrap: 'wrap',
            gap: 6
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent)' }}>
              📋 ANALISA HARGA SATUAN (per {form.unit || 'satuan'})
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Button size="xs" variant="info" onClick={() => setShowPreset(!showPreset)}>
                📦 Preset
              </Button>
              <Button size="xs" variant="success" onClick={() => {
                setShowAddComp(true)
                setShowPreset(false)
              }}>
                ➕ Manual
              </Button>
            </div>
          </div>

          {/* Presets Panel */}
          {showPreset && (
            <div style={{
              padding: 10, background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius)', marginBottom: 10, border: '1px solid var(--border)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>KLIK UNTUK MENAMBAHKAN:</div>
                <button
                  style={{ fontSize: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
                  onClick={() => { setShowPreset(false); setShowPresetMgr(true) }}
                >
                  ⚙️ Kelola Preset Saya
                </button>
              </div>
              {['bahan', 'upah', 'alat'].map(type => {
                const tc = TYPE_COLORS[type]
                // Gabung: user preset di atas (ditandai ★), lalu hardcoded
                const userItems = (userPresets[type] || []).map(p => ({ ...p, isCustom: true }))
                const builtIn = PRESETS[type].filter(
                  bp => !userItems.some(u => u.name.toLowerCase() === bp.name.toLowerCase())
                )
                const allItems = [...userItems, ...builtIn]
                return (
                  <div key={type} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: tc.c, marginBottom: 4 }}>{tc.label}</div>
                    <div className="preset-grid">
                      {allItems.map((p, i) => (
                        <button
                          key={i}
                          className="preset-btn"
                          onClick={() => addPreset(p, type)}
                          style={p.isCustom ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : {}}
                          title={p.isCustom ? '★ Preset milik Anda' : 'Preset bawaan'}
                        >
                          {p.isCustom ? '★ ' : ''}{p.name} ({fmt(p.price)})
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Add Component Form */}
          {showAddComp && (
            <div style={{
              padding: 10,
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius)',
              marginBottom: 10,
              border: '1px solid var(--accent)'
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>
                Tambah Komponen:
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tipe</label>
                  <select
                    className="form-select"
                    value={compForm.type}
                    onChange={(e) => setCompForm({ ...compForm, type: e.target.value })}
                  >
                    <option value="bahan">🧱 Bahan</option>
                    <option value="upah">👷 Upah</option>
                    <option value="alat">🔧 Alat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Satuan</label>
                  <input
                    className="form-input"
                    value={compForm.unit}
                    onChange={(e) => setCompForm({ ...compForm, unit: e.target.value })}
                    placeholder="sak, OH, m³, kg"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Nama *</label>
                <input
                  className="form-input"
                  value={compForm.name}
                  onChange={(e) => setCompForm({ ...compForm, name: e.target.value })}
                  placeholder="Contoh: Semen PC 50kg"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Koefisien (per {form.unit || 'satuan'} pekerjaan) *</label>
                <input
                  className="form-input"
                  type="text"
                  inputMode="decimal"
                  value={compForm.coefficient}
                  onChange={(e) => setCompForm({ ...compForm, coefficient: e.target.value })}
                  placeholder="Contoh: 0.38, 8, 0.005"
                />
                <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>
                  ⮑ Berapa {compForm.unit || 'satuan'} {compForm.name || 'ini'} per 1 {form.unit || 'satuan'} pekerjaan?
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Harga Anggaran</label>
                  <MoneyInput
                    value={compForm.price}
                    onChange={(v) => setCompForm({ ...compForm, price: v })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Harga Realisasi</label>
                  <MoneyInput
                    value={compForm.real_price}
                    onChange={(v) => setCompForm({ ...compForm, real_price: v })}
                  />
                </div>
              </div>
              {compForm.coefficient && compForm.price ? (
                <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700, marginBottom: 8 }}>
                  Subtotal: {fmtCoef(parseFloat(compForm.coefficient) || 0)} × {fmt(pN(compForm.price))} =
                  {' '}{fmt((parseFloat(compForm.coefficient) || 0) * pN(compForm.price))}
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="sm" variant="primary" onClick={addComp}>
                  ✓ Tambahkan
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setShowAddComp(false)}>
                  Batal
                </Button>
              </div>
            </div>
          )}

          {/* Component List */}
          {itemComponents.length > 0 ? (
            <div className="comp-list">
              {['bahan', 'upah', 'alat'].map(type => {
                const comps = itemComponents.filter(c => c.type === type)
                if (!comps.length) return null

                const tc = TYPE_COLORS[type]
                const subTotal = comps.reduce((s, c) => s + c.coefficient * c.price, 0)

                return (
                  <div key={type} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: tc.c, marginBottom: 4 }}>
                      {tc.label} — Sub: {fmt(subTotal)}/{form.unit}
                    </div>
                    {comps.map(c => (
                      <div key={c.id} className="comp-item">
                        <span className="ci-type" style={{ background: tc.bg, color: tc.c }}>
                          {type === 'bahan' ? '🧱' : type === 'upah' ? '👷' : '🔧'}
                        </span>
                        <span className="ci-name">{c.name}</span>
                        <span className="ci-detail">
                          {fmtCoef(c.coefficient)} {c.unit} × {fmt(c.price)}
                        </span>
                        <span className="ci-sub">= {fmt(c.coefficient * c.price)}</span>
                        <button className="ci-del" onClick={() => delComp(c.id)}>×</button>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: 16,
              color: 'var(--text-muted)',
              fontSize: 12
            }}>
              Belum ada komponen. Gunakan <b>Preset</b> atau <b>Manual</b> untuk menambahkan bahan, upah, dan alat.
            </div>
          )}

          {/* Summary */}
          {itemComponents.length > 0 && (
            <div style={{
              marginTop: 10,
              padding: 10,
              background: 'var(--accent-bg)',
              borderRadius: 'var(--radius)',
              border: '1px solid rgba(245,158,11,.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                  Harga Satuan Anggaran
                </span>
                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--accent)' }}>
                  {fmt(formBudgetUP)} / {form.unit}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--info)' }}>
                  Harga Satuan Realisasi
                </span>
                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--info)' }}>
                  {fmt(formRealUP)} / {form.unit}
                </span>
              </div>
              <div style={{
                borderTop: '1px solid var(--border)',
                paddingTop: 6,
                marginTop: 6,
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: 11 }}>
                  Total Anggaran: {fmtN(pN(form.volume))} × {fmt(formBudgetUP)}
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent)' }}>
                  {fmt(formBudgetTotal)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ fontSize: 11 }}>
                  Total Realisasi: {fmtN(pN(form.real_volume))} × {fmt(formRealUP)}
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--info)' }}>
                  {fmt(formRealTotal)}
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Kelola Preset */}
      <PresetManager
        show={showPresetMgr}
        onClose={() => { setShowPresetMgr(false); fetchData() }}
      />
    </div>
  )
}